import { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Clock, Download, Share2, ChevronLeft, ChevronRight } from 'lucide-react';
import { 
  FaPlay, 
  FaPause, 
  FaBackwardStep, 
  FaForwardStep
} from "react-icons/fa6";

import type { Message } from '../components/TextToSpeechApp';
import { useIsDarkMode } from '@/hooks/useIsDarkMode';

const STATIC_IMAGES = [
  'https://static.photos/blurred/1024x576',
  'https://static.photos/vintage/1024x576',
  'https://static.photos/gradient/1024x576',
  'https://static.photos/minimal/1024x576',
];

let globalImageQueue = [...STATIC_IMAGES];
let globalImagePointer = 0;

const getNextPrimaryBackground = () => {
  if (globalImageQueue.length === 0 || globalImagePointer >= globalImageQueue.length) {
    globalImageQueue = [...STATIC_IMAGES].sort(() => Math.random() - 0.5);
    globalImagePointer = 0;
  }

  const img = globalImageQueue[globalImagePointer];
  globalImagePointer += 1;
  return img;
};

const getRandomStaticPhotoUrl = (baseUrl: string) => {
  const rand = Math.random().toString(36).slice(2);
  return `${baseUrl}${baseUrl.includes('?') ? '&' : '?'}r=${rand}`;
};

const getTwoFallbackUrls = (baseUrl: string) => {
  const first = getRandomStaticPhotoUrl(baseUrl);
  const second = getRandomStaticPhotoUrl(baseUrl);
  return first === second ? [first, getRandomStaticPhotoUrl(baseUrl)] : [first, second];
};

interface TimestampData {
  word: string;
  start: number;
  end: number;
  text_offset: number;
}

interface SingleAudioPlayerProps {
  message: Message;
  audioUrl: string;
  timestampsUrl?: string;
  voiceName: string;
}

export function SingleAudioPlayer({ message, audioUrl, timestampsUrl, voiceName }: SingleAudioPlayerProps) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [timestamps, setTimestamps] = useState<TimestampData[]>([]);
  const [highlightedWordIndex, setHighlightedWordIndex] = useState(-1);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [isUserScrolling, setIsUserScrolling] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);

  const audioRef = useRef<HTMLAudioElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const activeWordRef = useRef<HTMLSpanElement | null>(null);
  const scrollTimeoutRef = useRef<number | null>(null);

  const isDarkMode = useIsDarkMode();
  const [bgBaseUrl, setBgBaseUrl] = useState(() => getNextPrimaryBackground());
  const [bgCandidates, setBgCandidates] = useState(() => getTwoFallbackUrls(bgBaseUrl));
  const [bgCandidateIndex, setBgCandidateIndex] = useState(0);
  const backgroundImage = bgCandidates[bgCandidateIndex] ?? STATIC_IMAGES[0];

  const getVoiceFlag = (name: string | undefined) => {
    if (!name) return '';
    const nameLower = name.toLowerCase();
    
    const american = ['autumn', 'melody', 'hannah', 'emily', 'ivy', 'kaitlyn', 'luna', 'willow', 'lauren', 'sierra', 'noah', 'jasper', 'caleb', 'ronan', 'ethan', 'daniel', 'zane'];
    const chinese = ['mei', 'lian', 'ting', 'jing', 'wei', 'jian', 'hao', 'sheng'];
    const spanish = ['lucía', 'mateo', 'javier'];
    const french = ['élodie'];
    const hindi = ['ananya', 'priya', 'arjun', 'rohan'];
    const italian = ['giulia', 'luca'];
    const portuguese = ['camila', 'thiago', 'rafael'];
    const sarvam = ['shubh', 'aditya', 'ritu', 'priya', 'neha', 'rahul', 'pooja', 'rohan', 'simran', 'kavya', 'amit', 'dev', 'ishita', 'shreya', 'ratan', 'varun', 'manan', 'sumit', 'roopa', 'kabir', 'aayan', 'ashutosh', 'advait', 'anand', 'tanya', 'tarun', 'sunny', 'mani', 'gokul', 'vijay', 'shruti', 'suhani', 'mohit', 'kavitha', 'rehan', 'soham', 'rupali'];

    if (american.includes(nameLower)) return '🇺🇸';
    if (chinese.includes(nameLower)) return '🇨🇳';
    if (spanish.includes(nameLower)) return '🇪🇸';
    if (french.includes(nameLower)) return '🇫🇷';
    if (hindi.includes(nameLower)) return '🇮🇳';
    if (italian.includes(nameLower)) return '🇮🇹';
    if (portuguese.includes(nameLower)) return '🇵🇹';
    if (sarvam.includes(nameLower)) return '🇮🇳';

    return '';
  };

  useEffect(() => {
    const fetchTimestamps = async () => {
      if (timestampsUrl) {
        try {
          const response = await fetch(timestampsUrl);
          const timestampData = await response.json();
          setTimestamps(timestampData);
        } catch (error) {
          console.error('Failed to fetch timestamps:', error);
        }
      }
    };
    fetchTimestamps();
  }, [timestampsUrl]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const updateTime = () => {
      setCurrentTime(audio.currentTime);
      const currentWordIndex = timestamps.findIndex((timestamp, index) => {
        const nextTimestamp = timestamps[index + 1];
        return audio.currentTime >= timestamp.start && 
               (!nextTimestamp || audio.currentTime < nextTimestamp.start);
      });
      setHighlightedWordIndex(currentWordIndex);
    };

    const updateDuration = () => setDuration(audio.duration || 0);
    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    const handleEnded = () => {
      setIsPlaying(false);
      setHighlightedWordIndex(-1);
      setCurrentTime(0);
    };

    audio.addEventListener('timeupdate', updateTime);
    audio.addEventListener('loadedmetadata', updateDuration);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('timeupdate', updateTime);
      audio.removeEventListener('loadedmetadata', updateDuration);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [timestamps]);

  useEffect(() => {
    if (isPlaying && !isUserScrolling && activeWordRef.current && scrollContainerRef.current) {
      activeWordRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [highlightedWordIndex, isPlaying, isUserScrolling]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      setIsUserScrolling(true);
      if (scrollTimeoutRef.current !== null) {
        window.clearTimeout(scrollTimeoutRef.current);
      }
      scrollTimeoutRef.current = window.setTimeout(() => {
        setIsUserScrolling(false);
      }, 1500);
    };

    container.addEventListener('scroll', handleScroll);
    return () => {
      container.removeEventListener('scroll', handleScroll);
      if (scrollTimeoutRef.current !== null) {
        window.clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.playbackRate = playbackSpeed;
    }
  }, [playbackSpeed]);

  const togglePlayback = () => {
    if (!audioRef.current) return;
    isPlaying ? audioRef.current.pause() : audioRef.current.play();
  };

  const shareAudio = async () => {
    if (!audioUrl) return;
    if (!audioUrl.startsWith('data:')) {
      if (navigator.share) {
        try {
          await navigator.share({
            title: 'Aawaz AI - Audio',
            text: message.text?.substring(0, 100) || 'Check out this audio',
            url: audioUrl
          });
          return;
        } catch (err) {}
      }
      try {
        await navigator.clipboard.writeText(audioUrl);
        setShareCopied(true);
        setTimeout(() => setShareCopied(false), 2000);
        return;
      } catch {}
    }
    
    if (navigator.share) {
      try {
        const response = await fetch(audioUrl);
        const blob = await response.blob();
        const ext = audioUrl.startsWith('data:audio/wav') ? 'wav' : 'mp3';
        const file = new File([blob], `aawaz-audio-${Date.now()}.${ext}`, { type: blob.type });
        await navigator.share({ files: [file], title: 'Aawaz AI - Audio' });
        return;
      } catch {}
    }
    downloadAudio();
  };

  const handleSliderChange = (value: number[]) => {
    if (audioRef.current) {
      audioRef.current.currentTime = value[0];
      setCurrentTime(value[0]);
    }
  };

  const skipForward = () => {
    if (audioRef.current) audioRef.current.currentTime = Math.min(duration, currentTime + 10);
  };

  const skipBackward = () => {
    if (audioRef.current) audioRef.current.currentTime = Math.max(0, currentTime - 10);
  };

  const cycleSpeed = () => {
    const speeds = [1, 1.25, 1.5, 2];
    const nextIndex = (speeds.indexOf(playbackSpeed) + 1) % speeds.length;
    setPlaybackSpeed(speeds[nextIndex]);
  };

  const downloadAudio = () => {
    if (!audioUrl) return;
    const link = document.createElement('a');
    link.href = audioUrl;
    link.download = `oreog-audio-${Date.now()}.mp3`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const formatTime = (time: number) => {
    if (isNaN(time)) return "0:00";
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  return (
    <div className={`flex flex-col md:flex-row bg-card border border-border shadow-xl rounded-2xl overflow-hidden w-full md:h-[350px]`}>
      {/* LEFT SIDE Visuals */}
      <div className="relative w-full h-36 md:w-2/5 md:h-full shrink-0 bg-black transition-all overflow-hidden select-none pointer-events-none">
        <img
          src={backgroundImage}
          alt="Audio background"
          className="absolute inset-0 w-full h-full object-cover"
          onError={() => {
            if (bgCandidateIndex === 0) {
              setBgCandidateIndex(1);
              return;
            }
            const nextBase = getNextPrimaryBackground();
            setBgBaseUrl(nextBase);
            setBgCandidates(getTwoFallbackUrls(nextBase));
            setBgCandidateIndex(0);
          }}
        />
        <div className="absolute inset-0 bg-black/40 md:bg-black/30" />
        <div className="absolute bottom-3 left-4 right-4 text-white">
          <h2 className="text-3xl font-bold tracking-tight text-white drop-shadow-md">Oreog</h2>
          <div className="flex items-center gap-2 text-white/80 text-xs sm:text-sm mt-1">
            <span className="text-sm sm:text-base">{getVoiceFlag(voiceName)}</span>
            <span className="font-medium truncate">{voiceName || 'AI Voice'}</span>
          </div>
        </div>
      </div>

      {/* RIGHT SIDE */}
      <div className="flex-1 flex flex-col w-full bg-background/50 backdrop-blur-sm overflow-hidden rounded-b-2xl sm:rounded-r-2xl rounded-bl-none relative">
        <div 
          ref={scrollContainerRef}
          className="flex-1 overflow-y-auto p-4 scrollbar-thin scrollbar-thumb-muted scrollbar-track-transparent [mask-image:linear-gradient(to_bottom,transparent,black_10%,black_90%,transparent)]"
          style={{ minHeight: '120px' }}
        >
          <div className="py-4">
            {timestamps.length === 0 ? (
              <div className="text-muted-foreground text-base max-h-[220px] sm:max-h-auto sm:text-lg leading-relaxed whitespace-pre-wrap px-1">
                {message.text}
              </div>
            ) : (
              <div className="flex flex-wrap gap-x-1.5 gap-y-1 content-start px-1">
                {timestamps.map((timestamp, index) => {
                  const isActive = index === highlightedWordIndex;
                  return (
                    <span
                      key={index}
                      ref={isActive ? activeWordRef : null}
                      onClick={() => {
                        if (audioRef.current) audioRef.current.currentTime = timestamp.start;
                      }}
                      className={`cursor-pointer transition-all duration-300 rounded px-1 py-0.5 text-base sm:text-lg leading-relaxed
                        ${isActive 
                          ? 'bg-primary/20 text-primary font-bold scale-105' 
                          : 'text-muted-foreground hover:text-foreground'
                        }
                      `}
                    >
                      {timestamp.word}
                    </span>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Player Controls */}
        <div className="shrink-0 p-3 sm:p-4 dark:bg-[#272727] bg-background z-20 relative">
          <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
            <span className="text-[10px] sm:text-xs font-mono text-muted-foreground w-8 sm:w-10 text-right">
              {formatTime(currentTime)}
            </span>
            <Slider
              value={[currentTime]}
              max={duration}
              step={0.1}
              onValueChange={handleSliderChange}
              className="flex-1 cursor-pointer"
            />
            <span className="text-[10px] sm:text-xs font-mono text-muted-foreground w-8 sm:w-10">
              {formatTime(duration)}
            </span>
          </div>

          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              size="sm"
              onClick={cycleSpeed}
              className="text-xs font-bold rounded-full text-muted-foreground hover:text-primary w-20 px-0"
            >
              <Clock className="w-4 h-4 sm:w-5 sm:h-5 mr-1" />
              {playbackSpeed}x
            </Button>

            <div className="flex items-center gap-2 sm:gap-4">
              <Button variant="ghost" size="icon" onClick={skipBackward} className="text-foreground/80 rounded-full hover:text-foreground h-8 w-8 sm:h-10 sm:w-10">
                <FaBackwardStep className="w-4 h-4 sm:w-5 sm:h-5" />
              </Button>
              <Button size="icon" onClick={togglePlayback} className="h-10 w-10 rounded-full bg-primary text-primary-foreground shadow-md transition-transform">
                {isPlaying ? <FaPause className="w-4 h-4" /> : <FaPlay className="w-4 h-4 ml-0.5" />}
              </Button>
              <Button variant="ghost" size="icon" onClick={skipForward} className="text-foreground/80 rounded-full hover:text-foreground h-8 w-8 sm:h-10 sm:w-10">
                <FaForwardStep className="w-4 h-4 sm:w-5 sm:h-5" />
              </Button>
            </div>

            <div className="flex items-center gap-1 sm:gap-2">
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={shareAudio} 
                className={`h-8 w-8 sm:h-10 sm:w-10 rounded-full transition-colors ${shareCopied ? 'text-green-400' : 'text-muted-foreground hover:text-foreground'}`}
              >
                <Share2 className="w-4 h-4 sm:w-5 sm:h-5" />
              </Button>
              <Button variant="ghost" size="icon" onClick={downloadAudio} className="h-8 w-8 rounded-full sm:h-10 sm:w-10">
                <Download className="w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground hover:text-primary" />
              </Button>
            </div>
          </div>
        </div>
      </div>
      <audio ref={audioRef} src={audioUrl} preload="metadata" />
    </div>
  );
}

interface AudioMessageProps {
  message: Message;
  onUpdateMessage: (messageId: string, audioUrl: string, timestampsUrl?: string) => void;
}
export function AudioMessage({ message }: AudioMessageProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  
  const dragStartRef = useRef<number | null>(null);
  const dragOffsetRef = useRef<number>(0);
  const [dragOffset, setDragOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  const audioResults = message.audioResults || [];

  if (audioResults.length <= 1) {
    const activeAudio = audioResults[0] || {
      voice: message.voiceSettings?.voice || 'AI Voice',
      audioUrl: message.audioUrl || '',
      timestampsUrl: message.timestampsUrl
    };
    if (!activeAudio.audioUrl) return null;

    return (
      <div className="w-full max-w-2xl mx-auto my-4 sm:my-6 animate-fade-in px-2 sm:px-0">
        <SingleAudioPlayer
          message={message}
          audioUrl={activeAudio.audioUrl}
          timestampsUrl={activeAudio.timestampsUrl}
          voiceName={activeAudio.voice}
        />
      </div>
    );
  }

  const handlePointerDown = (e: React.PointerEvent) => {
    const target = e.target as HTMLElement;
    if (
      target.closest('button') || 
      target.closest('a') || 
      target.closest('.cursor-pointer') ||
      target.closest('[role="slider"]') ||
      target.closest('.slider') ||
      target.closest('span[onClick]')
    ) {
      return;
    }

    if (e.button !== 0) return;
    dragStartRef.current = e.clientX;
    setIsDragging(true);
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (dragStartRef.current === null) return;
    const diff = e.clientX - dragStartRef.current;
    dragOffsetRef.current = diff;
    setDragOffset(diff);
  };

  const handlePointerUp = (e: React.PointerEvent) => {
    if (dragStartRef.current === null) return;
    e.currentTarget.releasePointerCapture(e.pointerId);
    
    const diffX = dragOffsetRef.current;
    dragStartRef.current = null;
    dragOffsetRef.current = 0;
    setDragOffset(0);
    setIsDragging(false);

    if (diffX < -80) {
      if (activeIndex < audioResults.length - 1) {
        setActiveIndex(prev => prev + 1);
      }
    } else if (diffX > 80) {
      if (activeIndex > 0) {
        setActiveIndex(prev => prev - 1);
      }
    }
  };

  const handlePrev = () => {
    if (activeIndex > 0) {
      setActiveIndex(prev => prev - 1);
    }
  };

  const handleNext = () => {
    if (activeIndex < audioResults.length - 1) {
      setActiveIndex(prev => prev + 1);
    }
  };

  return (
    <div className="w-full max-w-2xl mx-auto my-4 sm:my-6 animate-fade-in px-2 sm:px-0 relative group select-none">
      {/* Left Hover Button */}
      {activeIndex > 0 && (
        <button
          onClick={handlePrev}
          className="absolute left-2 top-1/2 -translate-y-1/2 z-30 bg-black/60 hover:bg-black/85 text-white rounded-full p-1.5 backdrop-blur-sm shadow-lg border border-white/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none group-hover:pointer-events-auto"
          aria-label="Previous voice model"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
      )}

      {/* Right Hover Button */}
      {activeIndex < audioResults.length - 1 && (
        <button
          onClick={handleNext}
          className="absolute right-2 top-1/2 -translate-y-1/2 z-30 bg-black/60 hover:bg-black/85 text-white rounded-full p-1.5 backdrop-blur-sm shadow-lg border border-white/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none group-hover:pointer-events-auto"
          aria-label="Next voice model"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      )}

      {/* Carousel Container */}
      <div 
        className="overflow-hidden w-full rounded-2xl touch-pan-y cursor-grab active:cursor-grabbing"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
      >
        <div 
          className="flex"
          style={{ 
            transform: `translateX(calc(-${activeIndex * 100}% + ${dragOffset}px))`,
            transition: isDragging ? 'none' : 'transform 0.4s cubic-bezier(0.25, 1, 0.5, 1)'
          }}
        >
          {audioResults.map((result, idx) => (
            <div key={idx} className="w-full shrink-0 px-0.5 pointer-events-none">
              {/* Enable child elements internally but wrap drag handle on container */}
              <div className="pointer-events-auto">
                <SingleAudioPlayer
                  message={message}
                  audioUrl={result.audioUrl}
                  timestampsUrl={result.timestampsUrl}
                  voiceName={result.voice}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Slide Pagination Dots */}
      <div className="flex justify-center items-center gap-1.5 mt-3">
        {audioResults.map((_, idx) => (
          <button
            key={idx}
            onClick={() => setActiveIndex(idx)}
            className={`transition-all duration-300 rounded-full ${
              idx === activeIndex 
                ? 'w-6 h-2 bg-primary' 
                : 'w-2 h-2 bg-muted-foreground/30 hover:bg-muted-foreground/50'
            }`}
            aria-label={`Go to slide ${idx + 1}`}
          />
        ))}
      </div>
    </div>
  );
}